import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { mintSuiteDeepLinkUrl } from "@/lib/suite/openSuiteApp";
import { activityService } from "@/services/analytics/ActivityService";
import { Agent } from "@/types/agent/agent";
import { getReadableGenieBrandTheme, withAlpha } from "@/utils/branding/genieBrandTheme";

import type { AgentMailboxStatus } from "@/types/agent-mailboxes";
interface GenieSuiteCardProps {
  agent: Agent;
  brand?: string;
}

const SYNC_APP_URL = "https://sync.helpgenie.ai/login";

/**
 * Opens this Genie's tools page in Help Genie Sync via SSO.
 * Opens a blank tab synchronously (inside the click handler) before the async
 * token mint, so iOS Safari doesn't block the popup — see openSuiteApp.ts.
 */
const openGenieSyncTools = async (genieId: string) => {
  activityService.logSuiteAppVisit("Sync", SYNC_APP_URL, "genie-suite-card");

  const win = window.open("about:blank", "_blank");
  if (win) {
    try {
      win.opener = null;
    } catch {
      /* cross-origin guard — safe to ignore */
    }
  }

  const url = await mintSuiteDeepLinkUrl(SYNC_APP_URL, `/app/genies/${genieId}`);
  if (win) win.location.href = url;
  else window.location.href = url;
};

type Tone = "live" | "attention" | "idle";

interface SuiteTile {
  key: "mail" | "sync" | "outbound";
  productWord: string;
  icon: GenieIconName;
  /** The one-word state shown as a pill. */
  stateLabel: string;
  tone: Tone;
  /** Plain-language line under the state. Always populated. */
  detail: string;
  /** When set, the detail line is copyable and copies this value instead of the detail text. */
  copyValue?: string;
  /** Primary action: opens the app through the in-app SSO explainer route. */
  open: { label: string; to: string };
  /** Optional secondary action inside Voice (manage / logs) or into a Suite app. */
  manage?: { label: string; to?: string; anchor?: string; onClick?: () => void };
}

const TONE_PILL: Record<Tone, string> = {
  live: "bg-genie-primary/10 text-genie-primary",
  attention: "bg-genie-secondary/10 text-genie-secondary",
  idle: "bg-gray-100 text-gray-600",
};

const MAILBOX_STATE: Record<AgentMailboxStatus, { label: string; tone: Tone }> = {
  provisioned: { label: "Active", tone: "live" },
  suspended: { label: "Paused", tone: "idle" },
  pending: { label: "Setting up", tone: "attention" },
  failed: { label: "Needs attention", tone: "attention" },
  unavailable: { label: "Unavailable", tone: "attention" },
};

const scrollToSection = (anchor: string) => {
  document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const copyToClipboard = async (value: string, message: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  } catch {
    toast.error("Failed to copy");
  }
};

/**
 * GenieSuiteCard — where this Genie works beyond calls and chat.
 *
 * The suite apps (Mail, Sync, Outbound) keep their own names and their own
 * tabs, but they are the same Genie. This card puts that on the Genie's page:
 * one tile per app, with whatever Voice can honestly see about it, and one
 * click into the app. Mail reads the Genie's own mailbox; Sync reads this
 * Genie's configured sync_tools (settings.sync_tools) — a local count, not a
 * live health check; Outbound reads agents.outbound_connected_at, which
 * Outbound's hg-gateway keeps current on connect/disconnect.
 */
export const GenieSuiteCard = ({ agent, brand = "#4E9CFF" }: GenieSuiteCardProps) => {
  const { primary, secondary } = getReadableGenieBrandTheme(agent);

  const mailbox = agent.mailbox;
  const mailState = mailbox ? MAILBOX_STATE[mailbox.status] : { label: "No mailbox yet", tone: "idle" as Tone };
  const mailDetail = mailbox?.email_address
    ? mailbox.test_mode
      ? `${mailbox.email_address} · test mode`
      : mailbox.email_address
    : mailbox
      ? (mailbox.last_error ?? "Address not assigned yet")
      : "Create one and this Genie answers its own support email";

  const syncTools = agent.settings?.sync_tools ?? [];
  const syncState: { label: string; tone: Tone } =
    syncTools.length > 0
      ? { label: `${syncTools.length} connected`, tone: "live" }
      : { label: "No tools yet", tone: "idle" };
  const syncDetail =
    syncTools.length > 0
      ? `${syncTools
          .map((t) => t.name)
          .slice(0, 3)
          .join(", ")}${syncTools.length > 3 ? ` and ${syncTools.length - 3} more` : ""}`
      : "Connect HubSpot, Slack, or your CRM so this Genie can act after a conversation";

  const outboundConnectedAt = agent.outbound_connected_at ?? null;

  const tiles: SuiteTile[] = [
    {
      key: "mail",
      productWord: "Mail",
      icon: "mail",
      stateLabel: mailState.label,
      tone: mailState.tone,
      detail: mailDetail,
      copyValue: mailbox?.email_address,
      open: { label: "Open Mail", to: "/genie-hub/channels/mail" },
      manage:
        mailbox?.status === "provisioned"
          ? { label: "Logs", to: `/genie-hub/my-genies/${agent.id}/mailbox-logs` }
          : { label: mailbox ? "Manage" : "Create mailbox", anchor: "section-mailbox" },
    },
    {
      key: "sync",
      productWord: "Sync",
      icon: "sync",
      stateLabel: syncState.label,
      tone: syncState.tone,
      detail: syncDetail,
      open: { label: "Open Sync", to: "/genie-hub/channels/sync" },
      manage: {
        label: syncTools.length > 0 ? "Manage tools" : "Connect a tool",
        onClick: () => void openGenieSyncTools(agent.id),
      },
    },
    {
      key: "outbound",
      productWord: "Outbound",
      icon: "phone-outgoing",
      stateLabel: outboundConnectedAt ? "Connected" : "Not connected",
      tone: outboundConnectedAt ? "live" : "idle",
      detail: outboundConnectedAt
        ? `Visitors can ask for a callback · since ${new Date(outboundConnectedAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`
        : "Connect this Genie in Outbound so visitors can ask it to call them back",
      open: { label: "Open Outbound", to: "/genie-hub/channels/outbound" },
    },
  ];

  return (
    <OverviewSectionCard
      title="Help Genie apps"
      subtitle="Where this Genie works beyond calls and chat. Same Genie, same knowledge, one login."
      genieIcon="apps"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="flex flex-col rounded-2xl border border-gray-200 bg-gray-50 p-4 transition-all hover:border-gray-300 hover:bg-white hover:shadow-sm"
          >
            <div className="flex items-center gap-2.5">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                style={{
                  background: `linear-gradient(135deg, ${withAlpha(primary, "1a")}, ${withAlpha(secondary, "1a")})`,
                }}
              >
                <GenieIcon
                  name={tile.icon}
                  size={18}
                  color={primary}
                  secondaryColor={secondary}
                  gradientEnabled={agent.branding?.gradientEnabled ?? true}
                  gradientAngle={agent.branding?.gradientAngle}
                />
              </span>
              <span className="min-w-0 text-sm font-bold text-[#222B45]">
                Help Genie{" "}
                <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                  {tile.productWord}
                </span>
              </span>
            </div>

            <span
              className={`mt-3 inline-flex self-start rounded-full px-2 py-0.5 text-xs font-medium ${TONE_PILL[tile.tone]}`}
            >
              {tile.stateLabel}
            </span>

            <p className="mt-2 flex-1 text-xs leading-snug text-gray-500" title={tile.detail}>
              {tile.key === "mail" && mailbox?.email_address ? (
                <>
                  <CopyableEmail email={mailbox.email_address} />
                  {mailbox.test_mode && " · test mode"}
                </>
              ) : (
                tile.detail
              )}
            </p>

            <div className="mt-3 flex items-center justify-between gap-2">
              <Link
                to={tile.open.to}
                className="inline-flex items-center gap-1 text-xs font-semibold text-genie-primary hover:underline"
              >
                {tile.open.label}
                <ArrowUpRight className="h-3 w-3" />
              </Link>
              {tile.manage &&
                (tile.manage.to ? (
                  <Link
                    to={tile.manage.to}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800"
                  >
                    {tile.manage.label}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      tile.manage?.onClick ? tile.manage.onClick() : tile.manage?.anchor && scrollToSection(tile.manage.anchor)
                    }
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800"
                  >
                    {tile.manage.label}
                    <ArrowRight className="h-3 w-3" />
                  </button>
                ))}
            </div>
          </div>
        ))}
      </div>
    </OverviewSectionCard>
  );
};
